import React, { useState, useEffect, useRef } from 'react';
import { TextField, Box, Typography, Paper, IconButton, InputAdornment, Divider, Button, Avatar, useMediaQuery } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { useUserContext } from '../../context/user_context/UserContext';
import { useAuthContext } from '../../context/auth/AuthContext';
import { chatBotPrompt } from '../../repository/OpenAIDAO';
import { Close, Person, SupportAgent } from '@mui/icons-material';
import { StringKeys } from '../../resources/strings/StringKeys';
import { useTranslation } from 'react-i18next';

const Chatbot = ({ show, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const { firebaseUser } = useUserContext();
  const { user } = useAuthContext();
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width:600px)');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (input.trim()) {
      const userMessage = { id: Date.now().toString(), prompt: input, sender: 'user' };
      setMessages((prevMessages) => [...prevMessages, userMessage]);
      setLoading(true);
      setInput('');

      try {
        const response = await chatBotPrompt(input);
        const botMessage = {
          id: Date.now().toString() + '_response',
          response: response || "Sorry, I couldn't process that request.",
          sender: 'bot',
        };
        setMessages((prevMessages) => [...prevMessages, botMessage]);
      } catch (error) {
        console.error("Error processing with OpenAI:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const getUserAvatar = () => {
    if (firebaseUser.profile_picture_url) {
      return (
        <img 
          src={firebaseUser.profile_picture_url} 
          alt="Profile" 
          style={{
            width: '32px',
            height: '32px',
            objectFit: 'cover',
            borderRadius: '50%'
          }}
        />
      );
    } else {
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: '#007bff',
          color: 'white',
          fontWeight: 'bold',
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          fontSize: '14px'
        }}>
          {firebaseUser.first_name[0].toUpperCase()}{firebaseUser.last_name[0].toUpperCase()}
        </div>
      );
    }
  };

  // Function to clean any remaining formatting from text
  const cleanFormattingSymbols = (text) => {
    if (!text) return "";
    return text
      .replace(/\*\*/g, '')  // Remove bold formatting
      .replace(/\*/g, '')    // Remove italic formatting
      .replace(/\_\_/g, '')  // Remove underline formatting
      .replace(/\_/g, '');   // Remove other underscores
  };

  if (!show || !firebaseUser) {
    return null;
  }

  return (
    <>
      {firebaseUser && user && show && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: isMobile ? 0 : '20px',
            right: isMobile ? 0 : '20px',
            width: isMobile ? '100%' : '350px',
            height: isMobile ? '100%' : '600px',
            display: 'flex',
            flexDirection: 'column',
            zIndex: '1000000',
            borderRadius: isMobile ? '0px' : '12px',
            overflow: 'hidden',
            bgcolor: 'background.paper',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: (theme) => theme.palette.mode === 'dark' 
                ? theme.palette.grey[900] 
                : theme.palette.primary.main,
              p: 2,
              borderBottom: 1,
              borderColor: (theme) => theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'transparent',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SupportAgent sx={{ 
                color: (theme) => theme.palette.mode === 'dark'
                  ? theme.palette.primary.main
                  : 'white',
                fontSize: 24 
              }} />
              <Typography 
                variant="h6"
                sx={{ 
                  color: (theme) => theme.palette.mode === 'dark'
                    ? theme.palette.primary.main
                    : 'white',
                  fontWeight: 500
                }}
              >
                BiziSmart {t(StringKeys.SUPPORT)}
              </Typography>
            </Box>
            <IconButton
              onClick={onClose}
              sx={{
                color: (theme) => theme.palette.mode === 'dark'
                  ? theme.palette.primary.main
                  : 'white',
                '&:hover': {
                  background: (theme) => theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.1)'
                    : 'rgba(0, 0, 0, 0.1)',
                },
              }}
            >
              <Close />
            </IconButton>
          </Box>

          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: 2,
              bgcolor: (theme) =>
                theme.palette.mode === 'dark' 
                  ? theme.palette.background.default
                  : theme.palette.grey[50],
            }}
          >
            <Box
              className="bot-message"
              sx={{
                display: 'flex',
                gap: 1.5,
                mb: 2,
              }}
            >
              <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                <SupportAgent sx={{ fontSize: 20 }} />
              </Avatar>
              <Box
                sx={{
                  maxWidth: '80%',
                  p: 1.5,
                  borderRadius: '12px',
                  borderTopLeftRadius: '4px',
                  bgcolor: (theme) =>
                    theme.palette.mode === 'dark'
                      ? theme.palette.grey[800]
                      : theme.palette.grey[100],
                }}
              >
                <Typography variant="body1">{cleanFormattingSymbols(t(StringKeys.HELLO_WELCOME_TO_BIZISMART_))}</Typography>
              </Box>
            </Box>

            {messages.map((msg) => (
              <Box
                key={msg.id}
                sx={{
                  display: 'flex',
                  gap: 1.5,
                  mb: 2,
                  flexDirection: msg.sender === 'user' ? 'row-reverse' : 'row',
                }}
              >
                {msg.sender === 'user' ? (
                  getUserAvatar()
                ) : (
                  <Avatar
                    sx={{
                      bgcolor: 'primary.main',
                      width: 32,
                      height: 32,
                    }}
                  >
                    <SupportAgent sx={{ fontSize: 20 }} />
                  </Avatar>
                )}
                <Box
                  sx={{
                    maxWidth: '80%',
                    p: 1.5,
                    borderRadius: '12px',
                    borderTopLeftRadius: msg.sender === 'user' ? '12px' : '4px',
                    borderTopRightRadius: msg.sender === 'user' ? '4px' : '12px',
                    bgcolor: msg.sender === 'user'
                      ? 'primary.main'
                      : (theme) => theme.palette.mode === 'dark'
                        ? theme.palette.grey[800]
                        : theme.palette.grey[100],
                    color: msg.sender === 'user' ? 'white' : 'text.primary',
                  }}
                >
                  <Typography variant="body1">
                    {cleanFormattingSymbols(msg.sender === 'user' ? msg.prompt : msg.response)}
                  </Typography>
                </Box>
              </Box>
            ))}
            <div ref={messagesEndRef} />
          </Box>

          {loading && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 2,
                bgcolor: (theme) =>
                  theme.palette.mode === 'dark'
                    ? theme.palette.background.default
                    : theme.palette.grey[50],
              }}
            >
              <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                <SupportAgent sx={{ fontSize: 20 }} />
              </Avatar>
              <Box className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </Box>
            </Box>
          )}

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              p: 2,
              bgcolor: (theme) =>
                theme.palette.mode === 'dark'
                  ? theme.palette.background.paper
                  : 'white',
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            <TextField
              placeholder={`${t(StringKeys.TYPE_YOUR_MESSAGE)}...`}
              variant="outlined"
              fullWidth
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={loading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      color="primary"
                      onClick={handleSubmit}
                      disabled={loading || !input.trim()}
                      sx={{
                        '&.Mui-disabled': {
                          color: 'action.disabled',
                        },
                      }}
                    >
                      <SendIcon />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: '24px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) =>
                      theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.2)'
                        : 'rgba(0, 0, 0, 0.1)',
                  },
                },
              }}
              autoComplete="off"
            />
          </Box>
        </Paper>
      )}
    </>
  );
};

export default Chatbot;